import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { atom, useAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { cloneDeep, isEqual } from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import type { MessageAttachment } from '@/components/Message/Message.types';
import {
  CATEGORY_ID,
  isKanatiCategory,
  isKanatiServiceTicket,
  LOGS_ACTION_ID,
  SUBCATEGORY_ID
} from '@/features/TicketCreateDialog/TicketCreateDialog.constants';
import { filterValidIpAddresses } from '@/features/TicketCreateDialog/TicketCreateDialog.utils';
import type { TicketStatusDialogServiceSubmission } from '@/features/TicketStatusDialogService';
import type { TicketStatusDialogThreatSubmission } from '@/features/TicketStatusDialogThreat';
import useHandleError from '@/hooks/useHandleError';
import {
  EMPTY_TICKET_DIALOG,
  EMPTY_TICKET_PAGE,
  EMPTY_TICKET_REFERENCE,
  EMPTY_TICKET_TEMPLATE,
  mapTicketToTicketCreate,
  mapTicketToTicketUpdate
} from '@/pages/Ticket';
import type {
  MessageCreateDTO,
  MessageDTO,
  MessageTemplatesDTO,
  MessageUpdateDTO,
  OwnerDTO,
  RecommendationDTO,
  RecommendationTemplatesDTO,
  Ticket,
  TicketDialogTemplate,
  TicketDTO,
  TicketTemplatesDTO,
  TicketWatcherDTO,
  TicketWatcherUpdateDTO,
  Watcher,
  WatcherDTO
} from '@/pages/Ticket/Ticket.types';
import { axiosClient } from '@/services/BaseApi.service';
import { userIsInternalAtom } from '@/store/user.store';
import { sanitizeMarkdownContent } from '@/utils/Common.utils';

import type { AttachmentFile } from '../Announcements/Announcements.types';
import { organizationsAtom, selectedOrganizationAtom } from '../Organizations';
import { clearTicketsSearchCache } from '../Tickets/Tickets.store';
import { AI_SOC_TEAM_QUEUE } from './Ticket.constants';
import { mapTicketDTO, mapTicketWatchersDTO } from './Ticket.utils';
import type { TicketCreateSubmission } from './TicketCreate/TicketCreate.types';

export const watchersDialogOptionsAtom = atom<WatcherDTO[]>([]);
export const watchersPageOptionsAtom = atom<WatcherDTO[]>([]);

// used for Service and Threat Tickets (Ticket Details, Ticket Create, Ticket Edit)
export const ticketAtom = atom<Ticket>(
  // @ts-expect-error ts(2769)
  EMPTY_TICKET_PAGE
);

// used for Service Tickets (Ticket Create Dialog)
export const ticketDialogAtom = atom<Ticket>(
  // @ts-expect-error ts(2769)
  EMPTY_TICKET_DIALOG
);

export const ticketDialogIsOpenAtom = atom<boolean>(false);
export const ticketDialogReferenceTicketAtom = atom(EMPTY_TICKET_REFERENCE);
export const ticketDialogTemplateAtom = atom<TicketDialogTemplate>(
  EMPTY_TICKET_TEMPLATE
);

export const ticketIsEditingAtom = atom<boolean | null>(null);
export const ticketMessagesAtom = atom<MessageDTO[]>([]);
export const ticketWatchersAtom = focusAtom(ticketAtom, (optic) =>
  optic.prop('ticket_watchers')
);
export const ticketErrorAtom = atom('');

/** *
 * message calls
 * */
export const useCreateMessage = (refetch?: () => void, reset?: () => void) => {
  const [ticket] = useAtom(ticketAtom);
  const [userIsInternal] = useAtom(userIsInternalAtom);
  const [ticketDialogReferenceTicket, setTicketDialogReferenceTicket] = useAtom(
    ticketDialogReferenceTicketAtom
  );
  const handleError = useHandleError();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: (requestBody: MessageCreateDTO): Promise<AxiosResponse> => {
      const { attachments, kanati_response_visibility, ...messageBody } =
        requestBody;
      const visibility = userIsInternal ? kanati_response_visibility : null;
      const isKanatiTicket =
        ticket.id === requestBody.ticket_id &&
        isKanatiServiceTicket(ticket.ticket_type, ticket.ticket_category?.id);
      // Kanati responses on other tickets cannot carry attachments; Kanati service tickets accept them
      const files = visibility != null && !isKanatiTicket ? [] : attachments;
      if (files && files.length > 0) {
        const formData = new FormData();
        formData.append('body', sanitizeMarkdownContent(requestBody.body));
        formData.append('ticket_id', requestBody.ticket_id.toString());

        if (requestBody.internal !== undefined) {
          formData.append('internal', requestBody.internal.toString());
        }

        if (requestBody.request_human_soc) {
          formData.append('request_human_soc', 'true');
        }

        if (visibility != null) {
          formData.append('kanati_response_visibility', visibility);
        }

        files.forEach((file: unknown) => {
          formData.append('attachments', file as File);
          const attachmentFile = file as AttachmentFile;

          // Build metadata
          const fileMetadata = {
            filename: attachmentFile.name,
            safe: attachmentFile.safe
          };
          formData.append('attachments_metadata', JSON.stringify(fileMetadata));
        });

        return axiosClient.post('/messages/with_attachments', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      return axiosClient.post('/messages/', {
        ...messageBody,
        body: sanitizeMarkdownContent(requestBody.body),
        ...(visibility != null && { kanati_response_visibility: visibility })
      });
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to create message.');

      // if the message is being created from a reference ticket
      setTicketDialogReferenceTicket(EMPTY_TICKET_REFERENCE);
    },
    onSuccess: () => {
      if (!ticketDialogReferenceTicket) {
        enqueueSnackbar(`Message Submitted`, {
          variant: 'success'
        });
      }

      // if the message is being created from a reference ticket
      setTicketDialogReferenceTicket(EMPTY_TICKET_REFERENCE);

      reset?.();
      refetch?.();
    }
  });

  return {
    createMessage: mutation.mutate,
    isLoadingCreateMessage: mutation.isPending
  };
};

export const useDeleteMessage = (refetch: () => void) => {
  const handleError = useHandleError();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (messageId: number) => {
      return axiosClient.delete(`/messages/${messageId}`);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to delete message.');
    },
    onSuccess: () => {
      enqueueSnackbar(`Message Deleted`, {
        variant: 'success'
      });
      refetch();
    }
  });
};

export const useUpdateMessage = (refetch: () => void) => {
  const handleError = useHandleError();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      id,
      requestBody,
      addFiles = [],
      deleteIds = []
    }: {
      id: number;
      requestBody: MessageUpdateDTO;
      addFiles?: MessageAttachment[] | File[];
      deleteIds?: number[];
    }): Promise<AxiosResponse> => {
      const formData = new FormData();

      if (requestBody.body) {
        formData.append('body', sanitizeMarkdownContent(requestBody.body));
      }

      if (requestBody.internal) {
        formData.append('internal', requestBody.internal?.toString());
      }

      if (addFiles.length > 0) {
        addFiles.forEach((file: MessageAttachment | Blob) => {
          formData.append('new_attachments', file as File);

          const attachmentFile = file as AttachmentFile;

          // Build metadata
          const fileMetadata = {
            filename: attachmentFile.name,
            safe: attachmentFile.safe
          };
          formData.append(
            'new_attachments_metadata',
            JSON.stringify(fileMetadata)
          );
        });
      }

      if (deleteIds.length > 0) {
        formData.append('deletable_attachment_ids', deleteIds.join(','));
      }

      if (formData.entries().next().value) {
        return axiosClient.put(`/messages/${id}/with_attachments`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      return axiosClient.put(`/messages/${id}`, requestBody);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update message.');
    },
    onSuccess: () => {
      enqueueSnackbar('Message Updated', {
        variant: 'success'
      });
      refetch();
    }
  });
};

/** *
 * ticket calls
 * */
export const useCreateTicketPage = () => {
  const handleError = useHandleError();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      ticket: ticketAtomData
    }: {
      data: TicketCreateSubmission;
      ticket: Ticket;
    }) => {
      const formData = new FormData();

      const modifiedData = {
        ...data,
        ticket_type: ticketAtomData.ticket_type,
        ticket_watchers: data.ticket_watchers.filter(
          (watcher) => watcher.userId !== 0
        )
      };

      const requestBody = mapTicketToTicketCreate(modifiedData as Ticket);
      const clonedData = cloneDeep(requestBody);
      delete clonedData?.attachments;

      formData.append('ticket', JSON.stringify(clonedData));
      requestBody?.attachments?.forEach((file) => {
        formData.append('attachments', file as AttachmentFile);

        const attachmentFile = file as AttachmentFile;

        // Build metadata
        const fileMetadata = {
          filename: attachmentFile.name,
          safe: attachmentFile.safe
        };
        formData.append('attachments_metadata', JSON.stringify(fileMetadata));
      });

      return axiosClient.post('/tickets/with_attachments', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to create ticket.');
    },
    onSuccess: (respData) => {
      sessionStorage.removeItem('draft');
      navigate(`/ticket/${respData.data.id}`);
      enqueueSnackbar('Ticket Created', {
        variant: 'success'
      });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    }
  });
};

export const useCreateTicketDialog = () => {
  const [, setTicketDialogIsOpen] = useAtom(ticketDialogIsOpenAtom);
  const [ticketDialogReferenceTicket, setTicketDialogReferenceTicket] = useAtom(
    ticketDialogReferenceTicketAtom
  );
  const [, setTicketDialogTemplate] = useAtom(ticketDialogTemplateAtom);
  const { createMessage } = useCreateMessage();
  const handleError = useHandleError();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      ticketCategory,
      ticketSubcategory
    }: {
      data: Ticket;
      ticketCategory: number;
      ticketSubcategory: number;
    }) => {
      // internal networks
      const ipAddressAdds =
        (data.custom_data &&
          filterValidIpAddresses(data.custom_data?.environments?.add)) ??
        '';
      const ipAddressRemoves =
        data.custom_data &&
        filterValidIpAddresses(data.custom_data?.environments?.remove ?? '');
      const IS_ENVIRONMENTS_CHANGE_REQUEST_TICKET =
        ticketCategory === CATEGORY_ID.CHANGE_REQUEST &&
        ticketSubcategory === SUBCATEGORY_ID.INTERNAL_NETWORKS;
      const HAS_ENVIRONMENTS_ADD_KEY_VALUE =
        ipAddressAdds && ipAddressAdds.ipAddressesArrayValid.length > 0;
      const HAS_ENVIRONMENTS_REMOVE_KEY_VALUE =
        ipAddressRemoves && ipAddressRemoves.ipAddressesArrayValid.length > 0;

      // logs restore/export
      const logsAction = data.custom_data?.logs_data?.action;
      const logsStartDate = dayjs(data.custom_data?.logs_data?.start_date)
        .utc()
        .format();
      const logsEndDate = dayjs(data.custom_data?.logs_data?.end_date)
        .utc()
        .format();

      const IS_LOG_RESTORE_EXPORT_TICKET =
        ticketCategory === CATEGORY_ID.LOG_RESTORE_EXPORT;
      const HAS_LOGS_DATA_DATE_KEY_VALUE =
        logsAction?.toString() === LOGS_ACTION_ID.RESTORE;

      // travel notification
      const travelStartDate = dayjs(
        data.custom_data?.travel_notification?.start_date
      )
        .utc()
        .format();
      const travelEndDate = dayjs(
        data.custom_data?.travel_notification?.end_date
      )
        .utc()
        .format();
      const travelLocations = data.custom_data?.travel_notification?.locations;

      const IS_TRAVEL_NOTIFICATION_TICKET =
        ticketCategory === CATEGORY_ID.CHANGE_REQUEST &&
        ticketSubcategory === SUBCATEGORY_ID.TRAVEL_NOTIFICATION;

      const requestBody = {
        ...data,
        team_queue:
          data.team_queue ||
          (isKanatiCategory(ticketCategory) ? AI_SOC_TEAM_QUEUE : 'SOC'), // default to SOC (or AI SOC for Kanati categories) if not set
        ticket_watchers: data.ticket_watchers.filter(
          (watcher) => watcher.userId !== 0
        ),
        ...((IS_ENVIRONMENTS_CHANGE_REQUEST_TICKET ||
          IS_LOG_RESTORE_EXPORT_TICKET ||
          IS_TRAVEL_NOTIFICATION_TICKET) && {
          custom_data: {
            ...(IS_ENVIRONMENTS_CHANGE_REQUEST_TICKET && {
              environments: {
                ...(HAS_ENVIRONMENTS_ADD_KEY_VALUE && {
                  add: ipAddressAdds?.ipAddressesArrayValid
                }),
                ...(HAS_ENVIRONMENTS_REMOVE_KEY_VALUE && {
                  remove: ipAddressRemoves?.ipAddressesArrayValid
                })
              }
            }),
            ...(IS_LOG_RESTORE_EXPORT_TICKET && {
              logs_data: {
                action: logsAction,
                ...(HAS_LOGS_DATA_DATE_KEY_VALUE && {
                  start_date: logsStartDate,
                  end_date: logsEndDate
                })
              }
            }),
            ...(IS_TRAVEL_NOTIFICATION_TICKET && {
              travel_notification: {
                user_email: data.custom_data?.travel_notification?.user_email,
                start_date: travelStartDate,
                end_date: travelEndDate,
                desired_action:
                  data.custom_data?.travel_notification?.desired_action,
                locations: travelLocations
              }
            })
          }
        })
      };

      const formData = new FormData();
      const mappedRequestBody = mapTicketToTicketCreate(requestBody);
      const clonedData = cloneDeep(mappedRequestBody);
      if (clonedData) delete clonedData.attachments;

      formData.append('ticket', JSON.stringify(clonedData));
      requestBody.attachments?.forEach((file) => {
        formData.append('attachments', file as AttachmentFile);

        const attachmentFile = file as AttachmentFile;

        // Build metadata
        const fileMetadata = {
          filename: attachmentFile.name,
          safe: attachmentFile.safe
        };
        formData.append('attachments_metadata', JSON.stringify(fileMetadata));
      });

      return axiosClient.post('/tickets/with_attachments', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to create ticket.');
    },
    onSuccess: (respData) => {
      setTicketDialogIsOpen(false);
      setTicketDialogTemplate(EMPTY_TICKET_TEMPLATE);
      navigate(`/ticket/${respData.data.id}`);
      enqueueSnackbar('Ticket Created', {
        variant: 'success'
      });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });

      // if a reference ticket was created
      if (ticketDialogReferenceTicket.checked) {
        setTicketDialogReferenceTicket({
          ...ticketDialogReferenceTicket,
          new_ticket_id: respData.data.id
        });

        createMessage({
          body:
            'New Linked Ticket ' +
            'https://scope.pondurance.com/ticket/' +
            respData.data.id +
            ' created',
          ticket_id: ticketDialogReferenceTicket.original_ticket_id,
          internal: false,
          request_human_soc: false,
          attachments: []
        });
      }
    }
  });
};

export const useDeleteTicket = () => {
  const [{ id: ticket_id }] = useAtom(ticketAtom);
  const handleError = useHandleError();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      return axiosClient.delete(`/tickets/${ticket_id}`);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to delete ticket.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      navigate('/tickets');
      enqueueSnackbar(`Ticket #${ticket_id} Deleted`, {
        variant: 'success'
      });
      clearTicketsSearchCache(queryClient);
    }
  });
};

// used for cross referencing Scope 1 conversation ids with new Scope 2 service ticket ids
export const useFetchLegacyTicket = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (requestBody: {
      legacy_conversation_id: string | undefined;
    }) => {
      return axiosClient.post(
        '/v2/tickets/search?page=1&page_size=1000',
        requestBody
      );
    },
    onError: () => {
      navigate(`/not-found`);
    },
    onSuccess: (respData: AxiosResponse) => {
      navigate(`/ticket/${respData?.data[0].id}`);
    }
  });
};

export const useFetchTicket = (ticketId: number, locationPathname: string) => {
  const [, setTicket] = useAtom(ticketAtom);
  const [, setTicketMessages] = useAtom(ticketMessagesAtom);
  const [, setTicketWatchers] = useAtom(ticketWatchersAtom);
  const navigate = useNavigate();
  const handleError = useHandleError();

  // redirect to not-found page if ticketId is NaN
  if (Number.isNaN(ticketId)) navigate('/not-found');

  const { data, error, isError, isFetching, isLoading, isSuccess, refetch } =
    useQuery<TicketDTO>({
      queryKey: [`tickets/${ticketId}`],
      enabled: ticketId !== -1
    });

  useEffect(() => {
    if (isError) {
      if (
        (error as AxiosError)?.response?.status === 404 &&
        locationPathname !== '/ticket/create'
      ) {
        navigate('/not-found');
      }
      handleError(error as AxiosError, 'Failure to fetch ticket.');
    }
    if (isSuccess) {
      setTicket(mapTicketDTO(data));
      setTicketMessages(data?.messages || []);
      setTicketWatchers(mapTicketWatchersDTO(data.ticket_watchers || []));
    }
  }, [data, isError, isSuccess]);

  return {
    isError,
    isFetching,
    isLoading,
    refetch
  };
};

export const useUpdateTicketPage = () => {
  const [ticket, setTicket] = useAtom(ticketAtom);
  const [, setTicketMessages] = useAtom(ticketMessagesAtom);
  const [, setTicketWatchers] = useAtom(ticketWatchersAtom);
  const handleError = useHandleError();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      ticket: ticketAtomData
    }: {
      data: TicketCreateSubmission;
      ticket: Ticket;
    }) => {
      const formData = new FormData();
      const modifiedData = {
        ...data,
        ticket_type: ticketAtomData.ticket_type,
        ticket_watchers: data.ticket_watchers.filter(
          (watcher) => watcher.userId !== 0
        )
      };

      const requestBody = mapTicketToTicketUpdate(modifiedData as Ticket);
      const clonedData = cloneDeep(requestBody);
      delete clonedData?.attachments;

      formData.append('ticket', JSON.stringify(clonedData));
      requestBody?.attachments?.forEach((file) => {
        if (file instanceof File) {
          const attachmentFile = file as AttachmentFile;
          formData.append('new_attachments', attachmentFile);

          // Build metadata
          const fileMetadata = {
            filename: attachmentFile.name,
            safe: attachmentFile.safe
          };
          formData.append(
            'new_attachments_metadata',
            JSON.stringify(fileMetadata)
          );
        }
      });

      const deleteIds: number[] = [];
      ticket.attachments?.forEach((file) => {
        if (!requestBody?.attachments?.some((obj) => isEqual(obj, file))) {
          deleteIds.push(file.id as number);
        }
      });

      if (deleteIds.length) {
        formData.append('deletable_attachment_ids', deleteIds.join(','));
      }

      return axiosClient.put(
        `/tickets/${ticket.id}/with_attachments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update ticket.');
    },
    onSuccess: ({ data }: { data: TicketDTO }) => {
      setTicket(mapTicketDTO(data));
      setTicketMessages(data?.messages || []);
      setTicketWatchers(mapTicketWatchersDTO(data.ticket_watchers || []));
      navigate(`/ticket/${data.id}`);
      enqueueSnackbar(`Ticket Updated`, {
        style: { whiteSpace: 'pre-line' },
        variant: 'success'
      });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      clearTicketsSearchCache(queryClient);
    }
  });
};

/** *
 * template calls
 * */
export const useFetchMessageTemplates = () => {
  const [userIsInternal] = useAtom(userIsInternalAtom);
  const handleError = useHandleError();

  const { refetch, isFetching, isError, data, error } = useQuery<
    MessageTemplatesDTO[]
  >({
    queryKey: ['templates/message'],
    enabled: userIsInternal,
    staleTime: 60000
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to load message templates.');
    }
  }, [isError]);

  return {
    refetch,
    isFetching,
    isError,
    data: data || []
  };
};

export const useFetchRecommendationTemplates = () => {
  const [userIsInternal] = useAtom(userIsInternalAtom);
  const handleError = useHandleError();

  const { refetch, isFetching, isError, data, error } = useQuery<
    RecommendationTemplatesDTO[]
  >({
    queryKey: ['templates/recommendation'],
    enabled: userIsInternal,
    staleTime: 60000
  });

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to load recommendation templates.'
      );
    }
  }, [isError]);

  return {
    refetch,
    isFetching,
    isError,
    data: data || []
  };
};

export const useFetchTicketTemplates = () => {
  const [userIsInternal] = useAtom(userIsInternalAtom);
  const handleError = useHandleError();

  const { refetch, isFetching, isError, data, error } = useQuery<
    TicketTemplatesDTO[]
  >({
    queryKey: ['templates/ticket'],
    enabled: userIsInternal,
    staleTime: 60000
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to load ticket templates.');
    }
  }, [isError]);

  return {
    refetch,
    isFetching,
    isError,
    data: data || []
  };
};

/** *
 * list calls
 * */
export const useFetchOwnersList = () => {
  const [userIsInternal] = useAtom(userIsInternalAtom);
  const handleError = useHandleError();

  const { refetch, data, error, isError, isFetching } = useQuery<OwnerDTO[]>({
    queryKey: [`users/owners`],
    enabled: userIsInternal,
    staleTime: 60 * 10 * 1000 // 10 minutes
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to load owners list.');
    }
  }, [isError]);

  return {
    refetch,
    isFetching,
    data: data || []
  };
};

/** *
 * list calls
 * */
export const useFetchAssigneesList = (orgLabel: string) => {
  const handleError = useHandleError();
  const { refetch, data, error, isError, isFetching, isPending } = useQuery<
    OwnerDTO[]
  >({
    queryKey: [`organizations/${orgLabel}/users?exclude_service_accounts=true`],
    staleTime: 60 * 10 * 1000, // 10 minutes,
    enabled: Boolean(
      orgLabel &&
        orgLabel.trim() !== '' &&
        orgLabel !== 'all' &&
        orgLabel !== 'noop'
    )
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to load assignees list.');
    }
  }, [isError]);

  return {
    refetch,
    isFetching,
    isPending,
    data: data || []
  };
};

/**
 * Fetches list of watchers options for watchers component
 * @param atomType - Choose which atom to update
 * @param orgLabelType - Choose if the org label is from the selected org or selected ticket
 * @returns Specified atom with a specified orgs watchers
 */
export const useFetchWatchersList = (
  atomType: 'dialog' | 'page',
  orgLabelType: 'selectedOrg' | 'selectedTicketOrg',
  ticketSelectedOrg?: number
) => {
  const [watchersDialogOptions, setWatchersDialogOptions] = useAtom(
    watchersDialogOptionsAtom
  );
  const [watchersPageOptions, setWatchersPageOptions] = useAtom(
    watchersPageOptionsAtom
  );
  const [organizations] = useAtom(organizationsAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [{ organization: selectedTicketOrganization }] = useAtom(ticketAtom);
  const handleError = useHandleError();

  const getOrgLabel = () => {
    if (ticketSelectedOrg) {
      return organizations.find((org) => org.id === ticketSelectedOrg)?.label;
    }
    if (orgLabelType === 'selectedOrg') {
      return selectedOrganization.label;
    }
    if (orgLabelType === 'selectedTicketOrg') {
      return selectedTicketOrganization?.label;
    }
    return null;
  };

  const orgLabel = getOrgLabel();

  const {
    isFetching,
    error: queryError,
    isError,
    isSuccess,
    data
  } = useQuery<WatcherDTO[]>({
    queryKey: [`organizations/${orgLabel}/watchers`],
    enabled: orgLabel !== 'all' && orgLabel !== undefined
  });

  useEffect(() => {
    if (isError) {
      handleError(queryError as AxiosError, 'Failure to load watchers list.');
    }
    if (isSuccess) {
      if (atomType === 'dialog') {
        setWatchersDialogOptions(data);
      }
      if (atomType === 'page') {
        setWatchersPageOptions(data);
      }
    }
  }, [data, isError, isSuccess]);

  return {
    isFetching,
    queryError,
    watchersDialogOptions,
    watchersPageOptions
  };
};

/** *
 * recommendation calls
 * */
export const useUpdateRecommendation = () => {
  const [ticket] = useAtom(ticketAtom);
  const handleError = useHandleError();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (recommendation: RecommendationDTO) => {
      const { id, body, completed } = recommendation;
      const requestBody = { body, completed };

      return axiosClient.put(
        `/tickets/${ticket.id}/recommendations/${id}`,
        requestBody
      );
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update ticket recommendation.');
    },
    onSuccess: () => {
      enqueueSnackbar('Recommendation Updated', {
        variant: 'success'
      });
    }
  });
};

/** *
 * status calls
 * */
export const useUpdateStatus = (
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const [ticket] = useAtom(ticketAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: (
      requestBody:
        | TicketStatusDialogServiceSubmission
        | TicketStatusDialogThreatSubmission
    ) => {
      return axiosClient.put(`/tickets/${ticket.id}/status`, requestBody);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update ticket status.');
    },
    onSuccess: () => {
      if (setOpen) setOpen(false);
      enqueueSnackbar('Ticket Status Updated', {
        variant: 'success'
      });
      queryClient.invalidateQueries({
        queryKey: [`tickets/${ticket.id}`]
      });
      clearTicketsSearchCache(queryClient);
    }
  });

  return {
    updateStatus: mutation.mutate,
    isLoadingUpdateStatus: mutation.isPending
  };
};

/** *
 * watchers calls
 * */
export const useUpdateTicketWatchers = (refetch: () => void) => {
  const [ticket] = useAtom(ticketAtom);
  const [, setTicketWatchers] = useAtom(ticketWatchersAtom);
  const handleError = useHandleError();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (watchers: Watcher[]) => {
      const watchersUpdate: TicketWatcherUpdateDTO[] = watchers.map((w) => {
        return { user_id: w.userId };
      });
      return axiosClient.put(`/tickets/${ticket.id}/watchers`, watchersUpdate);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update ticket watcher(s).');
    },
    onSuccess: ({ data }: { data: TicketWatcherDTO[] }) => {
      enqueueSnackbar('Watcher(s) Updated', {
        variant: 'success'
      });
      setTicketWatchers(mapTicketWatchersDTO(data));
      refetch();
    }
  });
};

/** *
 * next action calls
 * */
export const useUpdateNextAction = () => {
  const [ticket, setTicket] = useAtom(ticketAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (newOwner: string) => {
      const requestBody = {
        column: 'next_action',
        value: newOwner
      };
      return axiosClient.put(`/tickets/${ticket.id}/column`, requestBody);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update next action owner.');
    },
    onSuccess: (resp) => {
      setTicket({ ...ticket, next_action: resp.data.next_action });
      enqueueSnackbar('Next Action Owner Updated', {
        variant: 'success'
      });
      queryClient.invalidateQueries({ queryKey: [`tickets`] });
      queryClient.invalidateQueries({ queryKey: [`tickets/${resp.data.id}`] });
    }
  });
};

/** *
 * team queue calls
 * */
export const useUpdateTeamQueue = () => {
  const [ticket, setTicket] = useAtom(ticketAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (newTeamQueue: string) => {
      const requestBody = {
        column: 'team_queue',
        value: newTeamQueue
      };
      return axiosClient.put(`/tickets/${ticket.id}/column`, requestBody);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update team queue.');
    },
    onSuccess: (resp) => {
      setTicket({ ...ticket, team_queue: resp.data.team_queue });
      enqueueSnackbar('Team Queue Updated', {
        variant: 'success'
      });
      queryClient.invalidateQueries({ queryKey: [`tickets`] });
      queryClient.invalidateQueries({ queryKey: [`tickets/${resp.data.id}`] });
      clearTicketsSearchCache(queryClient);
    }
  });
};

/** *
 * assignee calls
 * */
export const useUpdateAssignee = () => {
  const [ticket] = useAtom(ticketAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (newAssigneeId: number | null) => {
      const requestBody = {
        column: 'assignee_id',
        value: newAssigneeId
      };
      return axiosClient.put(`/tickets/${ticket.id}/column`, requestBody);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update ticket assignee.');
    },
    onSuccess: (resp) => {
      enqueueSnackbar('Ticket Assignee Updated', {
        variant: 'success'
      });
      queryClient.invalidateQueries({ queryKey: [`tickets`] });
      queryClient.invalidateQueries({ queryKey: [`tickets/${resp.data.id}`] });
      clearTicketsSearchCache(queryClient);
    }
  });
};

/** *
 * followup date calls
 * */
export const useUpdateFollowupDate = () => {
  const [ticket, setTicket] = useAtom(ticketAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (newDay: string) => {
      const requestBody = {
        column: 'followup_date',
        value: newDay
      };
      return axiosClient.put(`/tickets/${ticket.id}/column`, requestBody);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update follow-up date.');
    },
    onSuccess: (resp) => {
      setTicket({ ...ticket, followup_date: resp.data.followup_date });
      enqueueSnackbar('Follow-up Date Updated', {
        variant: 'success'
      });
      queryClient.invalidateQueries({ queryKey: [`tickets`] });
      queryClient.invalidateQueries({ queryKey: [`tickets/${resp.data.id}`] });
    }
  });
};

export const useUpdateTags = () => {
  const [ticket, setTicket] = useAtom(ticketAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (tags: string[]) => {
      const requestBody = {
        column: 'tags',
        value: tags
      };
      return axiosClient.put(`/tickets/${ticket.id}/column`, requestBody);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update ticket tags.');
    },
    onSuccess: (resp) => {
      setTicket({ ...ticket, tags: resp.data.tags });
      enqueueSnackbar('Ticket Tags Updated', {
        variant: 'success'
      });
      queryClient.invalidateQueries({ queryKey: [`tickets/${resp.data.id}`] });
    }
  });
};

export const useUpdateCustomerTicketId = () => {
  const [ticket, setTicket] = useAtom(ticketAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (customerTicketId: string | null) => {
      const requestBody = {
        column: 'customer_ticket_id',
        value: customerTicketId
      };
      return axiosClient.put(`/tickets/${ticket.id}/column`, requestBody);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update customer ticket ID.');
    },
    onSuccess: (resp) => {
      setTicket({
        ...ticket,
        customer_ticket_id: resp.data.customer_ticket_id
      });
      enqueueSnackbar('Customer Ticket ID Updated', {
        variant: 'success'
      });
      queryClient.invalidateQueries({ queryKey: [`tickets`] });
      queryClient.invalidateQueries({ queryKey: [`tickets/${resp.data.id}`] });
    }
  });
};

/** *
 * auto_close_enabled calls
 * */
export const useUpdateAutoCloseEnabled = () => {
  const [ticket, setTicket] = useAtom(ticketAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (autoCloseEnabled: boolean) => {
      const requestBody = {
        column: 'auto_close_enabled',
        value: autoCloseEnabled
      };

      return axiosClient.put(`/tickets/${ticket.id}/column`, requestBody);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update auto-close setting.');
    },
    onSuccess: (resp) => {
      setTicket({
        ...ticket,
        auto_close_enabled: resp.data.auto_close_enabled
      });
      enqueueSnackbar(
        resp.data.auto_close_enabled
          ? 'Auto-close enabled for this ticket'
          : 'Auto-close disabled for this ticket',
        {
          variant: 'success'
        }
      );
      queryClient.invalidateQueries({ queryKey: [`tickets/${resp.data.id}`] });
    }
  });
};
